import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, Stepper, Step, StepButton, StepLabel, Tooltip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Check, CheckCircle, Email, WhatsApp, InfoOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { BarItem, ChannelSet, NotNeededReason, WizardStep, WIZARD_STEPS, stepNumber } from '../pickerWizard';

// A reserved line's content when it has nothing to say (a no-break space, written as a code point).
const NBSP = String.fromCharCode(0xA0);

// r4c — the page (the document) scrolls, and the wizard's steps differ in height, so the vertical scrollbar came and
// went between steps and when a source was picked; on Windows it takes ~17px of width, and every centred circle and
// button moved sideways. While the wizard is on screen the scrollbar is ALWAYS shown: `overflow-y: scroll` on <html>,
// through a CLASS (never inline). MUI's scroll lock for a Modal (the sender-number Select's menu, the R1 dialog)
// supports exactly this (ModalManager: when <html> has overflow-y scroll it hides THAT scrollbar and pads <body> by
// the same width — net zero) and restores only the inline style it set, so the class survives. r4b's
// `scrollbar-gutter: stable` did not: the gutter stayed while MUI padded the body, and the content shrank by 17px.
const STABLE_SCROLLBAR = 'smartSendWizardScrollbar';
const useGlobalStyles = makeStyles({ '@global': { [`html.${STABLE_SCROLLBAR}`]: { overflowY: 'scroll' } } });
export const useStableScrollbar = (): void => {
    useGlobalStyles();
    useEffect(() => {
        const root = document.documentElement;
        root.classList.add(STABLE_SCROLLBAR);
        return () => root.classList.remove(STABLE_SCROLLBAR);
    }, []);
};

// FU-06A (Idan) — the drawing pieces of the picker's wizard: the step bar, the card of the active step, the
// three channel cards, the «המקור קובע» note and the bottom bar. They decide nothing: every value they show
// and every step they allow comes from `wizardView` (pickerWizard.ts). New, and used by SmartSendPicker only.
// Visual reference: the ETX "קמפיין חדש" wizard (Idan's screenshots), inside Pulseem's theme.

const useStyles = makeStyles((theme) => ({
    barCard: {
        background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #ececec)', borderRadius: 12,
        padding: theme.spacing(0.5, 1), marginBottom: theme.spacing(2),
    },
    stepper: {
        background: 'transparent', padding: theme.spacing(1.5, 1), justifyContent: 'space-between', flexWrap: 'nowrap',
    },
    // Each step takes an equal share and may shrink; a label that does not fit is cut with an ellipsis (its full
    // text in `title`), so labels never run into each other in a longer language.
    step: { flex: '1 1 0', minWidth: 0, padding: theme.spacing(0, 0.5) },
    // r4 (Idan: «כל 5 הצעדים ממורכזים בתא שלהם», and zero shift) — the same button box for every step, and in it ONE
    // group — the circle, then the step's label — CENTRED in the cell. The group is as wide as the circle plus the
    // step's own label, and that label (its text and its 600 weight) never changes with the state; the second line is
    // kept out of the group's width (`sub`). So the group, and the circle in it, stand still in every state — a ✓ step
    // used to sit at the cell's edge while the others were centred (≈160px apart). Narrow screens: the circle alone.
    stepBtn: { justifyContent: 'center', textAlign: 'start' },
    stepBtnInert: { cursor: 'default', '&:hover': { background: 'transparent' } },
    stepLabelRoot: { width: 'auto', maxWidth: '100%', alignItems: 'center' },
    labelContainer: { minWidth: 0, flex: '0 1 auto' },
    // Narrow screens (labels hidden): no gap after the circle, so the group is the circle alone and sits at the centre.
    iconContainer: { [theme.breakpoints.down('xs')]: { paddingLeft: 0, paddingRight: 0 } },
    // Two lines, always: the label (20px) and the second line (16px), reserved even when empty.
    labelBox: {
        display: 'block', minWidth: 0,
        // Narrow screens: the numbers only, so the bar never scrolls sideways. The labels stay for screen readers.
        [theme.breakpoints.down('xs')]: {
            position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', border: 0,
        },
    },
    label: {
        display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        height: 20, lineHeight: '20px',
    },
    dot: {
        width: 26, height: 26, borderRadius: '50%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, lineHeight: 1,
        border: '1px solid var(--pls-d-border, #c4c4c4)', color: 'var(--pls-d-text-2, #5b6b7b)', background: 'var(--pls-d-surface, #fff)',
    },
    dotActive: { background: theme.palette.primary.main, borderColor: theme.palette.primary.main, color: '#fff' },
    dotDone: { background: 'var(--pls-d-surface-3, #1b1b1b)', borderColor: 'var(--pls-d-surface-3, #1b1b1b)', color: '#fff' },
    // A step this send does not have: grey, dashed, its number kept.
    dotOff: { background: 'var(--pls-d-surface-2, #f3f3f3)', borderColor: 'var(--pls-d-border, #d6d6d6)', borderStyle: 'dashed', color: 'var(--pls-d-text-2, #a8a8a8)' },
    labelOff: { color: 'var(--pls-d-text-2, #a8a8a8)' },
    // The second line under a label («לא נדרש», «לפי שלב ②», an automatic ②'s choice): small, regular weight, fixed
    // height — and NO width of its own (`width: 0; min-width: 100%` adds nothing to the group's intrinsic width), so
    // its text changing never re-centres the group; a text longer than the label runs on past it, visibly.
    sub: {
        display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--pls-d-text-2, #7a8794)',
        height: 16, lineHeight: '16px', whiteSpace: 'nowrap', overflow: 'visible',
        width: 0, minWidth: '100%',
    },
    summary: {
        color: 'var(--pls-d-text-2, #5b6b7b)', margin: theme.spacing(-1, 0.5, 2),
        height: 20, lineHeight: '20px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    card: {
        background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #ececec)', borderRadius: 12,
        padding: theme.spacing(3, 3.5),
        [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) },
    },
    // The question takes the focus (by script) on a step change made from the keyboard (r3; `arrivalFocus`). No CSS
    // frame for a script focus — also against a global `:focus` rule — and a ring only on `:focus-visible`.
    question: {
        fontWeight: 700,
        outline: 'none',
        '&:focus:not(:focus-visible)': { outline: 'none !important', boxShadow: 'none !important' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.light}`, outlineOffset: 4, borderRadius: 4 },
    },
    skLine: { height: 12, borderRadius: 6, background: 'var(--pls-d-surface-2, #eee)' },
    hint: { color: 'var(--pls-d-text-2, #5b6b7b)', marginTop: theme.spacing(0.5), marginBottom: theme.spacing(2) },
    // r3 — centred, «חזרה» then «המשך» side by side: the floating Pulsi bubbles sit in both bottom corners, where
    // the buttons used to be.
    nav: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap',
        marginTop: theme.spacing(2.5),
    },
    channels: {
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    // `height: 100%`: a card fills its grid cell, also inside the dimmed card's Tooltip span.
    chCard: {
        position: 'relative', boxSizing: 'border-box', minWidth: 0, height: '100%', padding: theme.spacing(2.5, 2),
        border: '2px solid var(--pls-d-border, #e0e0e0)', borderRadius: 10, cursor: 'pointer', outline: 'none', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing(1),
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: theme.palette.primary.light },
        '&:focus, &:focus-visible': { boxShadow: `0 0 0 3px ${theme.palette.primary.light}` },
    },
    chOn: { borderColor: theme.palette.primary.main, background: 'rgba(0, 0, 0, 0.02)' },
    chOff: {
        cursor: 'not-allowed', opacity: 0.55,
        '&:hover': { borderColor: 'var(--pls-d-border, #e0e0e0)' },
        '&:focus, &:focus-visible': { boxShadow: 'none' },
    },
    chCheck: { position: 'absolute', top: 8, insetInlineEnd: 8 },
    chIcons: { display: 'flex', gap: theme.spacing(0.5), '& svg': { fontSize: 30 } },
    chLabel: { fontWeight: 700 },
    chReason: { color: 'var(--pls-d-text-2, #5b6b7b)' },
    // The ETX "tip": light yellow, an info glyph, one line.
    note: {
        display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1),
        background: 'var(--pls-d-surface-2, #fbf8dc)', border: '1px solid var(--pls-d-border, #efe7a6)', borderRadius: 8,
        padding: theme.spacing(1.25, 2), marginTop: theme.spacing(2),
    },
    noteIcon: { color: 'var(--pls-d-warning, #7a6a12)', marginTop: 2, flexShrink: 0 },
    reviewRow: {
        display: 'flex', alignItems: 'baseline', gap: theme.spacing(1.5), flexWrap: 'wrap',
        padding: theme.spacing(1.25, 0), borderBottom: '1px solid var(--pls-d-border, #f0f0f0)',
    },
    reviewLabel: { color: 'var(--pls-d-text-2, #5b6b7b)', minWidth: 120 },
    reviewValue: { fontWeight: 600, overflowWrap: 'anywhere', flex: 1, minWidth: 0 },
    reviewExtra: { color: 'var(--pls-d-text-2, #5b6b7b)', fontWeight: 400 },
    srOnly: {
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
    },
}));

// ── the step bar ─────────────────────────────────────────────────────────────────────────────────────────
interface StepperProps {
    items: BarItem[];                              // always the five steps, numbered 1..5 (`wizardView().items`)
    active: WizardStep | null;   // null: no current step yet (the loading skeleton)
    label: (k: WizardStep) => string;
    secondary?: (k: WizardStep) => string | null;  // a second line under a label (an automatic ②'s choice)
    notNeededLabel: string;                        // «לא נדרש»
    notNeededTip: (reason: NotNeededReason) => string;
    pendingLabel: string;                          // «לפי שלב ②» — under ③/④ while ② is not answered yet
    ariaLabel: string;
    lockedTip: string;
    summary?: React.ReactNode;
    summaryTitle?: string | null;   // the summary as plain text, for the hover title of a cut line
    onJump: (k: WizardStep) => void;
    frozen?: boolean;   // no jumps at all (while Continue is in flight)
}

export const WizardStepper: React.FC<StepperProps> = ({
    items, active, label, secondary, notNeededLabel, notNeededTip, pendingLabel, ariaLabel, lockedTip, summary, summaryTitle, onJump,
    frozen = false,
}) => {
    const classes = useStyles();
    const icon = (it: BarItem) => (
        <span
            className={clsx(classes.dot, it.display === 'active' && classes.dotActive, it.display === 'done' && classes.dotDone,
                it.display === 'notNeeded' && classes.dotOff)}
            aria-hidden
        >
            {it.display === 'done' ? <Check style={{ fontSize: 16 }} /> : it.number.toLocaleString()}
        </span>
    );
    return (
        <>
            <Box className={classes.barCard} component="nav" aria-label={ariaLabel}>
                <Stepper nonLinear activeStep={items.findIndex((it) => it.key === active)} connector={null as any} className={classes.stepper}>
                    {items.map((it) => {
                        const k = it.key;
                        const off = it.display === 'notNeeded';
                        const pending = it.display === 'pending';
                        const live = it.navigable && !frozen;
                        // `icon` goes on StepButton: Step clones its child with the step's NUMBER as `icon`, and the
                        // child's own prop wins there; StepButton then hands its `icon` on to StepLabel.
                        const dot = icon(it);
                        let sub: string | null = null;
                        if (it.sub === 'notNeeded') sub = notNeededLabel;
                        else if (it.sub === 'pending') sub = pendingLabel;
                        else if (it.sub === 'choice' && secondary) sub = secondary(k);
                        // r4 — ZERO LAYOUT SHIFT: every step has the SAME structure in every state (a StepButton, inert
                        // when it is not a jump: no ripple, not tabbable, aria-disabled, and a mouse press does not
                        // focus it), its group (circle + label) is centred and as wide as its own label, and the label is
                        // always two lines of fixed height — the second reserved even when empty and adding no width. So
                        // no state moves a circle, sideways or up and down; only the circle's content and colours change.
                        // The native title only where no Tooltip wraps the label (a locked or «לא נדרש» step has one).
                        const textInner = (
                            <span className={clsx(classes.labelBox, (off || pending) && classes.labelOff)} title={off || it.locked ? undefined : label(k)}>
                                <span className={classes.label}>{label(k)}</span>
                                <span className={classes.sub}>{sub || NBSP}</span>
                                {/* The reason, for the keyboard, a screen reader and a narrow screen (the Tooltip is hover only). */}
                                {off && it.reason ? <span className={classes.srOnly}>{` — ${notNeededTip(it.reason)}`}</span> : null}
                            </span>
                        );
                        let text = textInner;
                        if (off && it.reason) text = <Tooltip title={notNeededTip(it.reason)}>{textInner}</Tooltip>;
                        else if (it.locked) text = <Tooltip title={lockedTip}>{textInner}</Tooltip>;
                        return (
                            <Step
                                key={k}
                                className={classes.step}
                                completed={it.display === 'done'}
                            >
                                {/* r4b — the current step is announced as the current step (aria-current on the button
                                    itself), not as an unavailable button; the other inert steps stay aria-disabled. */}
                                <StepButton
                                    icon={dot}
                                    className={clsx(classes.stepBtn, !live && classes.stepBtnInert)}
                                    onClick={live ? () => onJump(k) : undefined}
                                    onMouseDown={live ? undefined : (e: React.MouseEvent) => e.preventDefault()}
                                    disableRipple={!live}
                                    tabIndex={live ? 0 : -1}
                                    aria-current={k === active ? 'step' : undefined}
                                    aria-disabled={live || k === active ? undefined : true}
                                >
                                    <StepLabel
                                        icon={dot}
                                        classes={{ root: classes.stepLabelRoot, labelContainer: classes.labelContainer, iconContainer: classes.iconContainer }}
                                    >
                                        {text}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
            </Box>
            {/* r4 — always there, one line of fixed height (ellipsis; the whole line in `title`): it never pushes the page
                down when it fills. */}
            <Typography variant="body2" className={classes.summary} title={summaryTitle || undefined}>{summary || NBSP}</Typography>
        </>
    );
};

// ── while the wizard waits (WhatsApp's answer, the URL source's row): the SAME step bar (five pending steps, frozen)
// with its reserved summary line, then a card with a spinner — so the switch to the wizard moves nothing (r4) ──
export const WizardSkeleton: React.FC<{ label: (k: WizardStep) => string; ariaLabel: string }> = ({ label, ariaLabel }) => {
    const classes = useStyles();
    const items: BarItem[] = WIZARD_STEPS.map((k) => ({
        key: k, number: stepNumber(k), display: 'pending' as const, reason: null, sub: null, navigable: false, locked: false,
    }));
    return (
        <Box role="status" aria-busy="true" aria-label={ariaLabel}>
            <WizardStepper
                items={items}
                active={null}
                label={label}
                notNeededLabel=""
                notNeededTip={() => ''}
                pendingLabel=""
                ariaLabel={ariaLabel}
                lockedTip=""
                onJump={() => undefined}
                frozen
            />
            <Box className={classes.card} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Box className={classes.skLine} style={{ width: '40%', height: 22 }} />
                <Box className={classes.skLine} style={{ width: '65%' }} />
                <CircularProgress size={24} />
            </Box>
        </Box>
    );
};

// ── the card of the active step ──────────────────────────────────────────────────────────────────────────
interface StepCardProps {
    question: string;
    hint?: string | null;
    headingRef?: (el: HTMLHeadingElement | null) => void;
    hidden?: boolean;
    children?: React.ReactNode;
}

// `hidden` ⇒ display:none, never unmounted: a step's grid keeps its fetch, its pin and its effects.
export const StepCard: React.FC<StepCardProps> = ({ question, hint, headingRef, hidden = false, children }) => {
    const classes = useStyles();
    return (
        <Box className={classes.card} style={hidden ? { display: 'none' } : undefined}>
            <Typography variant="h5" component="h2" className={classes.question} tabIndex={-1} ref={headingRef as any}>
                {question}
            </Typography>
            {hint ? <Typography variant="body2" className={classes.hint}>{hint}</Typography> : <Box style={{ height: 16 }} />}
            {children}
        </Box>
    );
};

// ── «המקור קובע» ────────────────────────────────────────────────────────────────────────────────────────
export const WizardNote: React.FC<{ text: string; action?: React.ReactNode }> = ({ text, action }) => {
    const classes = useStyles();
    return (
        <Box className={classes.note} role="status">
            <InfoOutlined fontSize="small" className={classes.noteIcon} />
            <Typography variant="body2">
                {text}
                {action ? <>{' '}{action}</> : null}
            </Typography>
        </Box>
    );
};

// ── step ② · the three cards ────────────────────────────────────────────────────────────────────────────
export interface ChannelCardView { set: ChannelSet; label: string; reasonText: string | null }
interface ChannelCardsProps {
    cards: ChannelCardView[];
    chosen: ChannelSet | null;
    ariaLabel: string;
    onChoose: (set: ChannelSet) => void;
}

export const ChannelCards: React.FC<ChannelCardsProps> = ({ cards, chosen, ariaLabel, onChoose }) => {
    const classes = useStyles();
    const refs = useRef<Array<HTMLDivElement | null>>([]);
    const live = cards.map((c, i) => (c.reasonText == null ? i : -1)).filter((i) => i > -1);
    const chosenIdx = cards.findIndex((c) => c.set === chosen && c.reasonText == null);
    const roving = chosenIdx > -1 ? chosenIdx : (live.length ? live[0] : -1);
    const move = (from: number, dir: 1 | -1) => {
        if (!live.length) return;
        const p = live.indexOf(from);
        refs.current[live[(p + dir + live.length) % live.length]]?.focus();
    };
    return (
        <Box className={classes.channels} role="radiogroup" aria-label={ariaLabel}>
            {cards.map((c, i) => {
                const off = c.reasonText != null;
                const on = !off && c.set === chosen;
                const card = (
                    <div
                        ref={(el) => { refs.current[i] = el; }}
                        className={clsx(classes.chCard, on && classes.chOn, off && classes.chOff)}
                        role="radio"
                        aria-checked={on}
                        aria-disabled={off ? true : undefined}
                        aria-label={c.label}
                        aria-describedby={off ? `smartsend-wiz-ch-${c.set}` : undefined}
                        tabIndex={!off && i === roving ? 0 : -1}
                        onMouseDown={off ? (e) => e.preventDefault() : undefined}
                        onClick={() => { if (!off) onChoose(c.set); }}
                        onKeyDown={(e) => {
                            if (off) return;
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChoose(c.set); }
                            else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); move(i, 1); }
                            else if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); move(i, -1); }
                        }}
                    >
                        {on && <CheckCircle className={classes.chCheck} color="primary" fontSize="small" />}
                        <span className={classes.chIcons} aria-hidden>
                            {c.set !== 'W' && <Email color={on ? 'primary' : 'inherit'} />}
                            {c.set !== 'E' && <WhatsApp color={on ? 'primary' : 'inherit'} />}
                        </span>
                        <Typography className={classes.chLabel}>{c.label}</Typography>
                        {off && <Typography variant="caption" className={classes.chReason} id={`smartsend-wiz-ch-${c.set}`}>{c.reasonText}</Typography>}
                    </div>
                );
                return off
                    ? <Tooltip key={c.set} title={c.reasonText as string}><span style={{ display: 'block', height: '100%' }}>{card}</span></Tooltip>
                    : <React.Fragment key={c.set}>{card}</React.Fragment>;
            })}
        </Box>
    );
};

// ── step ⑤ · one row per item, each with its «שנה» ─────────────────────────────────────────────────────
// `value` is the customer's own text (names with `_`, Latin, digits): rendered as an isolated run (<bdi>), and
// left-to-right when `ltr` (a phone number), so an RTL line does not reorder it.
export interface ReviewRowView { key: string; label: string; value: string; ltr?: boolean; extra?: string | null; changeTo?: WizardStep | null }
interface ReviewListProps {
    rows: ReviewRowView[];
    changeLabel: string;
    changeAria: (label: string) => string;
    onChange: (k: WizardStep) => void;
    disabled?: boolean;   // «שנה» inert (while Continue is in flight)
}

export const ReviewList: React.FC<ReviewListProps> = ({ rows, changeLabel, changeAria, onChange, disabled = false }) => {
    const classes = useStyles();
    return (
        <Box>
            {rows.map((r) => (
                <Box key={r.key} className={classes.reviewRow}>
                    <Typography variant="body2" className={classes.reviewLabel}>{r.label}</Typography>
                    <Typography variant="body1" className={classes.reviewValue}>
                        <bdi dir={r.ltr ? 'ltr' : undefined}>{r.value}</bdi>
                        {r.extra ? <span className={classes.reviewExtra}>{` · ${r.extra}`}</span> : null}
                    </Typography>
                    {r.changeTo && (
                        <Button size="small" color="primary" disabled={disabled} onClick={() => onChange(r.changeTo as WizardStep)} aria-label={changeAria(r.label)}>
                            {changeLabel}
                        </Button>
                    )}
                </Box>
            ))}
        </Box>
    );
};

// ── the bottom bar: «חזרה» outlined, then the primary action, centred together (r3) ─────────────────────────
interface NavProps {
    backLabel: string;
    onBack?: (() => void) | null;
    backDisabled?: boolean;
    primaryLabel: string;
    onPrimary?: (() => void) | null;   // null ⇒ no primary on this step (⑤ has its own button in its card)
    primaryDisabled: boolean;
    busy?: boolean;
}

// r4 — a button's label while its action is in flight: the label keeps its box (transparent, so it is still the
// button's accessible name) and the spinner sits over it, so the button keeps its size and nothing around it moves.
export const BusyLabel: React.FC<{ busy: boolean; label: string }> = ({ busy, label }) => (
    <>
        <span style={busy ? { opacity: 0 } : undefined}>{label}</span>
        {busy ? (
            <CircularProgress size={18} color="inherit" style={{ position: 'absolute', top: 'calc(50% - 9px)', left: 'calc(50% - 9px)' }} />
        ) : null}
    </>
);

// r4 — both buttons are ALWAYS laid out; one that does not apply on this step is hidden in place (visibility), so
// neither moves when the other appears («חזרה» from ② on, no «המשך» on ⑤).
export const WizardNav: React.FC<NavProps> = ({ backLabel, onBack, backDisabled = false, primaryLabel, onPrimary, primaryDisabled, busy = false }) => {
    const classes = useStyles();
    const hidden = { visibility: 'hidden' as const };
    return (
        <Box className={classes.nav}>
            <Button
                variant="outlined"
                disabled={backDisabled || !onBack}
                onClick={onBack ?? undefined}
                style={onBack ? undefined : hidden}
                aria-hidden={onBack ? undefined : true}
                tabIndex={onBack ? undefined : -1}
            >
                {backLabel}
            </Button>
            <Button
                variant="contained"
                color="primary"
                disabled={primaryDisabled || !onPrimary}
                onClick={onPrimary ?? undefined}
                style={onPrimary ? undefined : hidden}
                aria-hidden={onPrimary ? undefined : true}
                aria-busy={busy && onPrimary ? true : undefined}
                tabIndex={onPrimary ? undefined : -1}
            >
                <BusyLabel busy={busy && !!onPrimary} label={primaryLabel} />
            </Button>
        </Box>
    );
};
